import { useState } from 'react';
import {
	useRequestAddAdminUser,
	useRequestBanUser,
	useRequestDeleteUser,
	useRequestEditUser,
	useRequestSuspendUser,
	useRequestUnsuspendUser,
	useRequestWarnUser
} from '@/hooks/httpRequest';
import { AdminUpdateActionEnum, AdminUserPermissionsEnum, BanTypeEnum, GenderEnum, UserAccountStatusEnum } from '@/lib/enum.ts';
import SuspendUserModal from '@/components/custom/userProfileDetails/SuspendUserModal.tsx';
import { useProfileUserStore } from '@/components/custom/profileDetailUserData/store/profileUserStore';
import { getTitleEnumValue, notify, notifyInternalMessage } from '@/functions';
import { confirm } from '@/components/custom/react-confirm/Confirm.tsx';
import { useFormSetCoinsToUser } from '@/hooks/forms';
import { useUserConnected } from '@/hooks';
import { SecuredComponent } from '@/components/hoc';
import { Button } from '@/components/ui/button.tsx';
import type { IHasUserData } from '@/interfaces';
import routes from '@/lib/routes';
import {
	isBannedOrDeleted,
	getUserCoinCount,
	isCertifPending,
	canBanDevice,
	isCertified,
	isBioValid,
	wait,
	cn
} from '@/lib/utils.ts';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, ShieldCheck, Loader2, Gift } from 'lucide-react';

const UserProfileDetailsButtonActions = ({ userData }: IHasUserData) => {
	const isViewedUserRoot = userData?.roles?.root === true;
	// a suspended member cannot see or act on what an admin sends them
	const isSuspended = userData?.accountStatus === UserAccountStatusEnum.SUSPENDED;

	const { triggerDeleteUser, isLoadingDeleteUser } = useRequestDeleteUser();
	const { triggerWarnUser, isLoadingWarnUser } = useRequestWarnUser();
	const { triggerEditUser, isLoadingEditUser } = useRequestEditUser();
	const { triggerBanUser, isLoadingBanUser } = useRequestBanUser();
	const { triggerSuspendUser, isLoadingSuspendUser } = useRequestSuspendUser();
	const { triggerUnsuspendUser, isLoadingUnsuspendUser } = useRequestUnsuspendUser();
	const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
	const { userData: storeUserData, setUserData } = useProfileUserStore();
	const { triggerAddAdminUser, isLoadingAddAdminUser } = useRequestAddAdminUser({
		onSuccess: () => {
			// Optimistically update the local state to show admin status
			// Full data refresh will happen on page reload
			if (userData) {
				setUserData({
					...userData,
					roles: { ...userData.roles, employee: true }
				});
			}
		}
	});
	// Use the store's userData for admin status to reflect optimistic updates
	const isUserAdmin = storeUserData?.roles?.employee ?? userData?.roles?.employee;
	const { openAddCreditsModal } = useFormSetCoinsToUser();
	const { userConnected } = useUserConnected();
	const navigate = useNavigate();

	const onUpdateBtnsPress = async (action: AdminUpdateActionEnum) => {
		let msg = '';

		if (action === AdminUpdateActionEnum.CHANGE_GENDER) {
			msg = 'Etes-vous sur de vouloir changer son genre ?';
		} else if (action === AdminUpdateActionEnum.REMOVE_BIO) {
			msg = 'Etes-vous sur de vouloir supprimer sa bio ?';
		} else if (action === AdminUpdateActionEnum.REMOVE_CERTIFICATION) {
			msg = 'Etes-vous sur de vouloir supprimer sa certification ?';
		} else if (action === AdminUpdateActionEnum.REQUEST_CERTIF) {
			msg = 'Etes-vous sur de vouloir exiger la certification de ce profil ?';
		}

		const yes = await confirm(msg);

		if (!yes) {
			return;
		}

		triggerEditUser({
			userId: userData?._id,
			action
		})
			.then(() => notify('Profil mis à jour.'))
			.catch(notifyInternalMessage);
	};

	const onWarnPress = async () => {
		const yes = await confirm('Etes-vous sur de vouloir avertir cet utilisateur ?');

		if (!yes) {
			return;
		}

		triggerWarnUser({ warnUserId: userData?._id })
			.then(() => notify('Profil a été averti.'))
			.catch(notifyInternalMessage);
	};

	const onBanPress = async (banType: BanTypeEnum) => {
		let msg = '';
		if (banType === BanTypeEnum.DEVICE_BAN) {
			msg = 'Etes-vous sur de vouloir bannir son téléphone ? Cet utilisateur ne pourra plus jamais utiliser Heyama';
		} else if (banType === BanTypeEnum.EMAIL_BAN) {
			msg = 'Etes-vous sur de vouloir bannir son adresse email ?';
		}

		const yes = await confirm(msg);

		if (!yes) {
			return;
		}

		triggerBanUser({ userId: userData?._id, banType })
			.then(() => notify('Le profil a été banni.'))
			.then(() => wait(userData?.pictures?.length || 1))
			.catch(notifyInternalMessage);
	};

	const onDeleteAccountPress = async () => {
		const yes = await confirm('Etes-vous sur de vouloir supprimer son compte ?');
		if (!yes) return;

		await triggerDeleteUser({ userId: userData?._id })
			.then(() => notify('Le profil a été spprimé avec succès.'))
			.catch(notifyInternalMessage);

		await wait(Math.min(userData?.pictures?.length || 1, 3));
	};

	const onSuspendPress = () => {
		setIsSuspendModalOpen(true);
	};

	const onSuspendConfirm = (reason: string, durationDays: number) => {
		setIsSuspendModalOpen(false);
		triggerSuspendUser({ userId: userData?._id, reason, durationDays })
			.then(() => notify('L\'utilisateur a été suspendu.'))
			.catch(notifyInternalMessage);
	};

	const onUnsuspendPress = async () => {
		const yes = await confirm('Etes-vous sur de vouloir lever la suspension de cet utilisateur ?');
		if (!yes) return;
		triggerUnsuspendUser({ userId: userData?._id })
			.then(() => notify('La suspension a été levée.'))
			.catch(notifyInternalMessage);
	};

	const handleOnClickBtnAddCredits = () =>
		userData &&
		openAddCreditsModal({
			user: {
				coins: getUserCoinCount(userData.coins, userData.diamondWallet?.coins),
				username: userData.username,
				id: userData._id
			}
		});

	const handleConvertToAdmin = async () => {
		if (!userData) return;
		const yes = await confirm('Etes-vous sur de vouloir convertir cet utilisateur en Admin ?');
		if (!yes) return;

		triggerAddAdminUser({ userId: userData._id, permissions: ['CERTIFICATION', 'PHOTO_VERIF', 'HISTORY']})
			.then(() => notify('Utilisateur converti en admin.'))
			.catch(notifyInternalMessage);
	};

	const handleConfigurePermissions = () => {
		navigate(routes.nav.settings.permissions.getHref());
	};

	if (!userData || isViewedUserRoot) {
		return null;
	}

	return (
		<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.PROFILE_DETAIL_ACTIONS]}>
			
				<span className={'text-center font-semibold text-sm'}>Actions disponibles</span>

				{isUserAdmin && <div className={'w-full text-center text-red-500 my-2'}>CET UTILISATEUR EST ADMIN</div>}

				<div className={cn('flex flex-wrap justify-center gap-4 px-2 animate__animated animate__fadeInUp', 'md:px-6')}>
					{userConnected?.root &&
						(isUserAdmin ? (
							<Button
								size={'sm'}
								variant={'secondary'}
								className="hover:scale-105 active:scale-95 transition-all duration-200"
								onClick={handleConfigurePermissions}
							>
								<ShieldCheck className="w-4 h-4 mr-2" />
								Configurer permissions
							</Button>
						) : (
							<Button
								size={'sm'}
								variant={'secondary'}
								disabled={isLoadingAddAdminUser || isSuspended}
								className="hover:scale-105 active:scale-95 transition-all duration-200"
								onClick={handleConvertToAdmin}
							>
								{isLoadingAddAdminUser ? (
									<Loader2 className="w-4 h-4 mr-2 animate-spin" />
								) : (
									<ShieldAlert className="w-4 h-4 mr-2" />
								)}
								Convertir en Admin
							</Button>
						))}

					{!isBannedOrDeleted(userData.accountStatus) && (
						<Button
							size={'sm'}
							disabled={isLoadingEditUser}
							variant={'secondary'}
							className="hover:scale-105 active:scale-95 transition-all duration-200"
							onClick={() => onUpdateBtnsPress(AdminUpdateActionEnum.CHANGE_GENDER)}
						>
							Changer sexe: {getTitleEnumValue('GenderEnum', userData.gender)} ➡️{' '}
							{getTitleEnumValue('GenderEnum', userData.gender === GenderEnum.MALE ? GenderEnum.FEMALE : GenderEnum.MALE)} 👩🏾‍🦱
						</Button>
					)}
					{!isBannedOrDeleted(userData.accountStatus) && isBioValid(userData.verifications) && (
						<Button
							size={'sm'}
							disabled={isLoadingEditUser}
							variant={'secondary'}
							className="hover:scale-105 active:scale-95 transition-all duration-200"
							onClick={() => onUpdateBtnsPress(AdminUpdateActionEnum.REMOVE_BIO)}
						>
							Supprimer sa Bio 📄
						</Button>
					)}
					{!isBannedOrDeleted(userData.accountStatus) && !isCertified(userData.verifications) && (
						<Button
							size={'sm'}
							variant={'secondary'}
							disabled={isLoadingEditUser || isSuspended || !!userData.incognito || userData.extra?.certificationRequired}
							className="hover:scale-105 active:scale-95 transition-all duration-200"
							onClick={() => onUpdateBtnsPress(AdminUpdateActionEnum.REQUEST_CERTIF)}
						>
							{userData.extra?.certificationRequired
								? 'Certification exigée 🤳🏾'
								: userData.incognito
									? 'Exiger la certification (Incognito) 🤳🏾'
									: isCertifPending(userData.verifications)
										? 'Certification en cours 🤳🏾'
										: 'Exiger la certification 🤳🏾'}
						</Button>
					)}
					{!isBannedOrDeleted(userData.accountStatus) && (
						<Button
							size={'sm'}
							variant={'secondary'}
							disabled={isLoadingDeleteUser}
							className="hover:scale-105 active:scale-95 transition-all duration-200"
							onClick={onDeleteAccountPress}
						>
							Supprimer son compte 🗑️
						</Button>
					)}
					<Button
						size={'sm'}
						variant={'secondary'}
						disabled={isLoadingBanUser}
						className="hover:scale-105 active:scale-95 transition-all duration-200"
						onClick={() => onBanPress(BanTypeEnum.EMAIL_BAN)}
					>
						Bannir son Email 📧
					</Button>
					{!isBannedOrDeleted(userData.accountStatus) && (
						<Button
							size={'sm'}
							disabled={isLoadingWarnUser || isSuspended}
							title={isSuspended ? 'Compte suspendu: impossible de l’avertir' : undefined}
							variant={'secondary'}
							className="hover:scale-105 active:scale-95 transition-all duration-200"
							onClick={onWarnPress}
						>
							Avertir ⚠️
						</Button>
					)}
					{!isBannedOrDeleted(userData.accountStatus) && isCertified(userData.verifications) && (
						<Button
							size={'sm'}
							disabled={isLoadingEditUser}
							variant={'secondary'}
							className="hover:scale-105 active:scale-95 transition-all duration-200"
							onClick={() => onUpdateBtnsPress(AdminUpdateActionEnum.REMOVE_CERTIFICATION)}
						>
							Supprimer sa certification ❌
						</Button>
					)}
					{!isBannedOrDeleted(userData.accountStatus) && (
						<SecuredComponent permissionsRequired={[
							AdminUserPermissionsEnum.ADD_REMOVE_USER_COINS,
							AdminUserPermissionsEnum.ADD_REMOVE_USER_INCOGNITO,
							AdminUserPermissionsEnum.ADD_REMOVE_USER_BOOST
						]}>
							<Button
								size={'sm'}
								variant={'secondary'}
								title="Ajouter des pièces, de l'incognito ou du boost à cet utilisateur"
								className="hover:scale-105 active:scale-95 transition-all duration-200"
								disabled={isSuspended}
								onClick={handleOnClickBtnAddCredits}
							>
								<Gift size={16} className="mr-1" />
								Ajouter des crédits
							</Button>
						</SecuredComponent>
					)}
					{canBanDevice(userData.deviceId) && (
						<Button
							size={'sm'}
							variant={'destructive'}
							disabled={isLoadingBanUser}
							className="hover:scale-105 active:scale-95 transition-all duration-200"
							onClick={() => onBanPress(BanTypeEnum.DEVICE_BAN)}
						>
							Bannir son Téléphone 📱
						</Button>
					)}
					<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.SUSPEND_USER]}>
						{userData.accountStatus === UserAccountStatusEnum.SUSPENDED ? (
							<Button
								size={'sm'}
								variant={'secondary'}
								disabled={isLoadingUnsuspendUser}
								className="bg-amber-500 hover:bg-amber-600 text-white hover:scale-105 active:scale-95 transition-all duration-200"
								onClick={onUnsuspendPress}
							>
								Lever la suspension
							</Button>
						) : (
							!isBannedOrDeleted(userData.accountStatus) && (
								<Button
									size={'sm'}
									variant={'destructive'}
									disabled={isLoadingSuspendUser}
									className="hover:scale-105 active:scale-95 transition-all duration-200"
									onClick={onSuspendPress}
								>
									Suspendre
								</Button>
							)
						)}
					</SecuredComponent>
				</div>
				<SuspendUserModal
					open={isSuspendModalOpen}
					onConfirm={onSuspendConfirm}
					onCancel={() => setIsSuspendModalOpen(false)}
				/>
		</SecuredComponent>
	);
};

export default UserProfileDetailsButtonActions;
